import { useState, useEffect } from 'react';
import { 
  Users, UserCheck, UserX, Award, Calendar, Search, 
  Filter, Download, RotateCcw, GraduationCap, ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import api, { classApi } from '../lib/api';

const EXIT_REASONS = [
  { value: 'hafiz', label: 'حافظ (Hafiz)', description: 'A complété tout le Coran', color: 'bg-green-100 text-green-700' },
  { value: 'transfer', label: 'Transfert', description: 'Transféré vers autre école', color: 'bg-blue-100 text-blue-700' },
  { value: 'family', label: 'Raison familiale', description: 'Décision de la famille', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'abandon', label: 'Abandon', description: 'A quitté sans compléter', color: 'bg-red-100 text-red-700' },
  { value: 'other', label: 'Autre', description: 'Autre raison', color: 'bg-gray-100 text-gray-700' },
];

export default function StudentsHistoryPage() {
  const [students, setStudents] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, exited: 0, hafiz: 0, by_exit_reason: {} });
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClass, setSelectedClass] = useState('all');
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [exitForm, setExitForm] = useState({
    exit_date: new Date().toISOString().split('T')[0],
    exit_reason: '',
    has_completed_quran: false,
  });

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    fetchStudents();
  }, [activeTab, selectedClass]);

  const fetchClasses = async () => {
    try {
      const res = await classApi.getAll({});
      setClasses(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchStudents = async () => {
    setLoading(true);
    try {
      let url = `/students/history/all?status=${activeTab}`;
      if (selectedClass && selectedClass !== 'all') {
        url += `&class_id=${selectedClass}`;
      }
      const res = await api.get(url);
      setStudents(res.data.students || []);
      setStats(res.data.stats || { total: 0, active: 0, exited: 0, hafiz: 0, by_exit_reason: {} });
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkExit = async () => {
    if (!selectedStudent || !exitForm.exit_reason) {
      toast.error('Veuillez sélectionner une raison de sortie');
      return;
    }

    try {
      await api.put(`/students/${selectedStudent.id}/exit`, null, {
        params: {
          exit_date: exitForm.exit_date,
          exit_reason: exitForm.exit_reason,
          has_completed_quran: exitForm.exit_reason === 'hafiz' || exitForm.has_completed_quran,
        }
      });
      toast.success('Élève marqué comme sorti');
      setShowExitDialog(false);
      setSelectedStudent(null);
      fetchStudents();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleReactivate = async (studentId) => {
    try {
      await api.put(`/students/${studentId}/reactivate`);
      toast.success('Élève réactivé');
      fetchStudents();
    } catch (error) {
      toast.error('Erreur lors de la réactivation');
    }
  };

  const openExitDialog = (student) => {
    setSelectedStudent(student);
    setExitForm({
      exit_date: new Date().toISOString().split('T')[0],
      exit_reason: '',
      has_completed_quran: false,
    });
    setShowExitDialog(true);
  };

  const getClassName = (classId) => {
    const cls = classes.find(c => c.id === classId);
    return cls?.name || 'N/A';
  };

  const getExitReasonConfig = (reason) => {
    return EXIT_REASONS.find(r => r.value === reason) || { label: reason, color: 'bg-gray-100' };
  };

  const filteredStudents = students.filter(s => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      s.first_name?.toLowerCase().includes(query) ||
      s.last_name?.toLowerCase().includes(query) ||
      s.matricule?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="students-history-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
            <Users className="w-7 h-7 text-primary" />
            Historique des Élèves
          </h1>
          <p className="text-muted-foreground text-sm">Suivi des entrées, sorties et Hafiz</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="cursor-pointer hover:bg-muted/50" onClick={() => setActiveTab('active')}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-600">{stats.active}</div>
                <p className="text-xs text-muted-foreground">Actifs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:bg-muted/50" onClick={() => setActiveTab('exited')}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <UserX className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-orange-600">{stats.exited}</div>
                <p className="text-xs text-muted-foreground">Sortis</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:bg-muted/50" onClick={() => setActiveTab('hafiz')}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Award className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">{stats.hafiz}</div>
                <p className="text-xs text-muted-foreground">Hafiz (حافظ)</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:bg-muted/50" onClick={() => setActiveTab('all')}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-600">{stats.total}</div>
                <p className="text-xs text-muted-foreground">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher par nom ou matricule..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="w-full sm:w-48">
              <Select value={selectedClass} onValueChange={setSelectedClass}>
                <SelectTrigger>
                  <SelectValue placeholder="Classe" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les classes</SelectItem>
                  {classes.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="active" className="gap-1">
            <UserCheck className="w-4 h-4" /> Actifs
          </TabsTrigger>
          <TabsTrigger value="exited" className="gap-1">
            <UserX className="w-4 h-4" /> Sortis
          </TabsTrigger>
          <TabsTrigger value="hafiz" className="gap-1">
            <Award className="w-4 h-4" /> Hafiz
          </TabsTrigger>
          <TabsTrigger value="all" className="gap-1">
            <Users className="w-4 h-4" /> Tous
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          {loading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16" />)}
            </div>
          ) : (
            <Card>
              <CardContent className="pt-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Élève</TableHead>
                      <TableHead>Classe</TableHead>
                      <TableHead>Date entrée</TableHead>
                      <TableHead>Date sortie</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredStudents.map((student) => {
                      const reasonConfig = getExitReasonConfig(student.exit_reason);
                      return (
                        <TableRow key={student.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{student.first_name} {student.last_name}</p>
                              <p className="text-xs text-muted-foreground">{student.matricule}</p>
                            </div>
                          </TableCell>
                          <TableCell>{getClassName(student.class_id)}</TableCell>
                          <TableCell>{student.entry_date || student.created_at?.split('T')[0] || '-'}</TableCell>
                          <TableCell>{student.exit_date || '-'}</TableCell>
                          <TableCell>
                            {student.is_active ? (
                              <Badge className="bg-blue-100 text-blue-700">Actif</Badge>
                            ) : student.has_completed_quran ? (
                              <Badge className="bg-green-100 text-green-700">
                                <Award className="w-3 h-3 mr-1" /> Hafiz
                              </Badge>
                            ) : (
                              <Badge className={reasonConfig.color}>{reasonConfig.label}</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {student.is_active ? (
                              <Button variant="outline" size="sm" onClick={() => openExitDialog(student)}>
                                <UserX className="w-4 h-4 mr-1" /> Marquer sortie
                              </Button>
                            ) : (
                              <Button variant="ghost" size="sm" onClick={() => handleReactivate(student.id)}>
                                <RotateCcw className="w-4 h-4 mr-1" /> Réactiver
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                {filteredStudents.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">Aucun élève trouvé</p>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Exit Dialog */}
      <Dialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Marquer la sortie de l'élève</DialogTitle>
            <DialogDescription>
              {selectedStudent && `${selectedStudent.first_name} ${selectedStudent.last_name}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Date de sortie</Label>
              <Input
                type="date"
                value={exitForm.exit_date}
                onChange={(e) => setExitForm({ ...exitForm, exit_date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Raison de sortie</Label>
              <Select value={exitForm.exit_reason} onValueChange={(v) => setExitForm({ ...exitForm, exit_reason: v, has_completed_quran: v === 'hafiz' })}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner une raison" />
                </SelectTrigger>
                <SelectContent>
                  {EXIT_REASONS.map(r => (
                    <SelectItem key={r.value} value={r.value}>
                      <div className="flex items-center gap-2">
                        {r.value === 'hafiz' && <Award className="w-4 h-4 text-green-600" />}
                        {r.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {exitForm.exit_reason === 'hafiz' && (
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-green-700 flex items-center gap-2">
                  <Award className="w-4 h-4" />
                  Cet élève sera marqué comme ayant complété tout le Coran (Hafiz)
                </p>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowExitDialog(false)}>Annuler</Button>
              <Button onClick={handleMarkExit}>Confirmer la sortie</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
